import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Badge, Button, DataTable, FilterChips, Modal, PageHeader, type ColumnDef, type FilterChipOption } from '@lipaflow/ui'
import { discountService, type Discount, type DiscountCreatePayload, type DiscountUpdatePayload } from '../services/admin'
import { usePageTitle } from '../lib/pageTitle'
import { useSector } from '../sector/sector'
import { apiErrorMessage } from '@lipaflow/shared'

type View = '' | 'active' | 'expired' | 'exhausted' | 'disabled'
const STATUS: Record<string, { label: string; variant: 'success' | 'neutral' | 'warning' }> = {
  active: { label: 'Active', variant: 'success' },
  expired: { label: 'Expired', variant: 'neutral' },
  exhausted: { label: 'Used up', variant: 'neutral' },
  disabled: { label: 'Switched off', variant: 'neutral' },
}
// The plans: how a customer pays. A code limited to some applies only to
// invoices in them (a year up front, say), and waits otherwise.
const CYCLES = [
  { value: 'monthly', label: 'Each month' },
  { value: 'termly', label: 'Each term' },
  { value: 'yearly', label: 'A year up front' },
]
const cycleLabel = (value: string) => CYCLES.find((c) => c.value === value)?.label ?? value

const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const labelCls = 'block text-caption text-ink-muted mb-1'

const money = (n: number | string) => `KES ${Number(n).toLocaleString('en-KE')}`
const describeValue = (d: Pick<Discount, 'discount_type' | 'value'>) =>
  d.discount_type === 'percentage' ? `${Number(d.value)}% off` : `${money(d.value)} off`
const date = (v: string) => new Date(v).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })

/** A server timestamp as a datetime-local value. An ISO string with a zone
 * renders blank in that input, so the edit form used to hide the expiry. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
/** Discounts — codes that take money off a customer's LipaFlow
 * subscription. Each belongs to one sector, and is unique there. */
export default function DiscountsPage() {
  usePageTitle('Discounts')
  const sector = useSector()
  const [view, setView] = useState<View>('')
  const [selected, setSelected] = useState<Discount | null>(null)
  const [creating, setCreating] = useState(false)
  const { data: discounts, isLoading } = useQuery({
    queryKey: ['discounts', sector.code, view],
    queryFn: () => discountService.list(sector.code, view || undefined),
  })

  const views: FilterChipOption<View>[] = [
    { key: '', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'expired', label: 'Expired' },
    { key: 'exhausted', label: 'Used up' },
    { key: 'disabled', label: 'Switched off' },
  ]
  const columns: ColumnDef<Discount>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (d) => (
        <div>
          <div className="font-mono font-medium text-ink">{d.code}</div>
          <div className="text-caption text-ink-muted line-clamp-1">{d.description}</div>
        </div>
      ),
    },
    { key: 'value', header: 'Discount', cell: (d) => <span className="text-body-sm text-ink tabular">{describeValue(d)}</span> },
    {
      key: 'used',
      header: 'Used',
      hideOnMobile: true,
      cell: (d) => <span className="text-body-sm text-ink-muted tabular">{d.times_used}{d.max_uses ? ` of ${d.max_uses}` : ' times'}</span>,
    },
    { key: 'until', header: 'Ends', hideOnMobile: true, cell: (d) => <span className="text-body-sm text-ink-muted">{d.valid_until ? date(d.valid_until) : 'No end date'}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (d) => {
        const s = STATUS[d.status] ?? { label: d.status, variant: 'neutral' as const }
        return <Badge variant={s.variant} size="sm">{s.label}</Badge>
      },
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Discounts"
        subtitle={`Codes that take money off a ${sector.name} customer's LipaFlow subscription. Each code belongs to ${sector.name} alone.`}
        actions={<Button leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setCreating(true)}>New discount code</Button>}
      />
      <FilterChips options={views} value={view} onChange={setView} />
      <DataTable
        data={discounts ?? []}
        columns={columns}
        getRowId={(d) => d.id}
        loading={isLoading}
        onRowClick={setSelected}
        emptyState={{
          title: view ? 'No codes in this view' : 'No discount codes yet',
          description: view ? 'Try another view.' : 'Create a code to offer customers money off.',
          action: view ? undefined : <Button onClick={() => setCreating(true)}>New discount code</Button>,
        }}
      />
      <DiscountPanel discount={selected} onClose={() => setSelected(null)} />
      <NewDiscountModal open={creating} onClose={() => setCreating(false)} />
    </div>
  )
}

function DiscountPanel({ discount, onClose }: { discount: Discount | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<DiscountUpdatePayload>({})
  const close = () => { setEditing(false); onClose() }
  const update = useMutation({
    mutationFn: (data: DiscountUpdatePayload) => discountService.update(discount!.id, data),
    onSuccess: () => { toast.success('Saved'); queryClient.invalidateQueries({ queryKey: ['discounts'] }); close() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't save")),
  })
  if (!discount) return <Modal open={false} onClose={close}>{null}</Modal>
  const s = STATUS[discount.status] ?? { label: discount.status, variant: 'neutral' as const }

  return (
    <Modal open onClose={close} title={discount.code} size="lg">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={s.variant}>{s.label}</Badge>
          <span className="text-body-sm text-ink">{describeValue(discount)}</span>
        </div>
        <p className="text-body-sm text-ink-muted">{discount.description}</p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Fact label="Used" value={`${discount.times_used}${discount.max_uses ? ` of ${discount.max_uses}` : ' times (no limit)'}`} />
          <Fact label="Runs" value={`${date(discount.valid_from)} – ${discount.valid_until ? date(discount.valid_until) : 'no end date'}`} />
          <Fact label="Largest discount" value={discount.max_discount_amount ? money(discount.max_discount_amount) : 'No cap'} />
          <Fact label="Uses per customer" value={String(discount.max_uses_per_tenant ?? 1)} />
          <Fact label="For customers who pay" value={discount.billing_cycles ? discount.billing_cycles.split(',').map(cycleLabel).join(', ') : 'Any way'} />
        </dl>

        {!editing ? (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-hairline">
            <Button
              variant="secondary"
              onClick={() => {
                setForm({
                  description: discount.description, max_uses: discount.max_uses,
                  max_discount_amount: discount.max_discount_amount, valid_until: toLocalInput(discount.valid_until) || null,
                })
                setEditing(true)
              }}
            >
              Edit
            </Button>
            {discount.status === 'active' && (
              <Button variant="ghost" onClick={() => update.mutate({ status: 'disabled' })} isLoading={update.isPending}>Switch off</Button>
            )}
            {discount.status === 'disabled' && (
              <Button variant="ghost" onClick={() => update.mutate({ status: 'active' })} isLoading={update.isPending}>Switch back on</Button>
            )}
          </div>
        ) : (
          <div className="pt-4 border-t border-hairline space-y-3">
            <label className="block"><span className={labelCls}>Description</span>
              <input value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block"><span className={labelCls}>Most uses in total</span>
                <input type="number" min={1} value={form.max_uses ?? ''} placeholder="No limit" onChange={(e) => setForm({ ...form, max_uses: e.target.value ? Number(e.target.value) : null })} className={field} />
              </label>
              <label className="block"><span className={labelCls}>Largest discount (KES)</span>
                <input type="number" min={0} value={form.max_discount_amount ?? ''} placeholder="No cap" onChange={(e) => setForm({ ...form, max_discount_amount: e.target.value ? Number(e.target.value) : null })} className={field} />
              </label>
              <label className="block"><span className={labelCls}>Ends</span>
                <input type="datetime-local" value={form.valid_until ?? ''} onChange={(e) => setForm({ ...form, valid_until: e.target.value || null })} className={field} />
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(false)}>Back</Button>
              <Button onClick={() => update.mutate(form)} isLoading={update.isPending}>Save</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

const emptyForm: DiscountCreatePayload = {
  code: '', description: '', discount_type: 'percentage', value: 0, max_uses: null, max_uses_per_tenant: 1, min_seats: null,
  min_amount: null, max_discount_amount: null, valid_from: null, valid_until: null, billing_cycles: null,
}

function NewDiscountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const sector = useSector()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<DiscountCreatePayload>({ ...emptyForm })
  const [cycles, setCycles] = useState<string[]>([])
  const reset = () => { setForm({ ...emptyForm }); setCycles([]) }
  const create = useMutation({
    mutationFn: () => discountService.create(sector.code, {
      ...form, code: form.code.toUpperCase(),
      billing_cycles: cycles.length ? cycles.join(',') : null,
    }),
    onSuccess: () => { toast.success('Discount code created'); queryClient.invalidateQueries({ queryKey: ['discounts'] }); reset(); onClose() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't create the code")),
  })
  const num = (v: string) => (v ? Number(v) : null)
  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v])
  const pct = form.discount_type === 'percentage'

  return (
    <Modal open={open} onClose={onClose} title={`New ${sector.name} discount code`} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block"><span className={labelCls}>Code</span>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })} placeholder="e.g. EARLYBIRD20" className={clsx(field, 'font-mono')} />
          </label>
          <label className="block"><span className={labelCls}>What it's for</span>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. 20% off the first term" className={field} />
          </label>
          <label className="block"><span className={labelCls}>Kind</span>
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className={field}>
              <option value="percentage">A percentage off</option>
              <option value="fixed">A fixed amount off</option>
            </select>
          </label>
          <label className="block"><span className={labelCls}>{pct ? 'Percentage' : 'Amount (KES)'}</span>
            <input type="number" min={0} max={pct ? 100 : undefined} value={form.value || ''} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} placeholder={pct ? 'e.g. 20' : 'e.g. 5000'} className={field} />
          </label>
          <label className="block"><span className={labelCls}>Starts (optional)</span>
            <input type="datetime-local" value={form.valid_from ?? ''} onChange={(e) => setForm({ ...form, valid_from: e.target.value || null })} className={field} />
          </label>
          <label className="block"><span className={labelCls}>Ends (optional)</span>
            <input type="datetime-local" value={form.valid_until ?? ''} onChange={(e) => setForm({ ...form, valid_until: e.target.value || null })} className={field} />
          </label>
        </div>

        <details className="rounded-card border border-hairline">
          <summary className="cursor-pointer px-4 py-3 text-body-sm font-medium text-ink">More limits</summary>
          <div className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block"><span className={labelCls}>Most uses in total</span>
                <input type="number" min={1} value={form.max_uses ?? ''} placeholder="No limit" onChange={(e) => setForm({ ...form, max_uses: num(e.target.value) })} className={field} />
              </label>
              <label className="block"><span className={labelCls}>Uses per customer</span>
                <input type="number" min={1} value={form.max_uses_per_tenant ?? 1} onChange={(e) => setForm({ ...form, max_uses_per_tenant: Number(e.target.value) || 1 })} className={field} />
              </label>
              <label className="block"><span className={labelCls}>Needs at least this many seats</span>
                <input type="number" min={1} value={form.min_seats ?? ''} placeholder="No minimum" onChange={(e) => setForm({ ...form, min_seats: num(e.target.value) })} className={field} />
              </label>
              <label className="block"><span className={labelCls}>Needs a bill of at least (KES)</span>
                <input type="number" min={0} value={form.min_amount ?? ''} placeholder="No minimum" onChange={(e) => setForm({ ...form, min_amount: num(e.target.value) })} className={field} />
              </label>
              <label className="block sm:col-span-2"><span className={labelCls}>Largest discount (KES)</span>
                <input type="number" min={0} value={form.max_discount_amount ?? ''} placeholder="No cap" onChange={(e) => setForm({ ...form, max_discount_amount: num(e.target.value) })} className={field} />
              </label>
            </div>
            <fieldset>
              <legend className={labelCls}>Only for customers who pay (none ticked = any way)</legend>
              <div className="flex flex-wrap gap-4">
                {CYCLES.map((c) => (
                  <label key={c.value} className="inline-flex items-center gap-2 text-body-sm text-ink">
                    <input type="checkbox" checked={cycles.includes(c.value)} onChange={() => toggle(cycles, setCycles, c.value)} /> {c.label}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        </details>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => create.mutate()}
            disabled={form.code.length < 2 || !form.description.trim() || !form.value}
            isLoading={create.isPending}
          >
            Create code
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-caption text-ink-faint">{label}</dt>
      <dd className="text-body-sm text-ink mt-0.5">{value}</dd>
    </div>
  )
}
